// src/components/LossRunDashboard/LossRunDashboard.tsx
// v2.0 - Professional Report Layout with Top-Down Hierarchy
// Changes:
// 1. Executive Summary bar at top (Risk Narrative + KPIs inline)
// 2. Loss Development and Limit Capacity side-by-side
// 3. Semantic coloring in Claims Ledger (red=severe, yellow=caution, green=healthy)

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment,
  LinearProgress, CircularProgress, Drawer, Stack, Skeleton
} from '@mui/material';
import {
  Close as CloseIcon, Search as SearchIcon, Download as DownloadIcon,
  AutoAwesome as MagicWandIcon, Send as SendIcon, Warning as WarningIcon,
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
  TrendingUp as TrendingUpIcon, Assessment as AssessmentIcon
} from '@mui/icons-material';

// Types
interface LossRunDashboardProps {
  augmentedPolicy: any;
  record?: any;
  policies?: any[];
  onClose: () => void;
}

interface Claim {
  loss_number: string;
  date_of_loss: string;
  product: string;
  total_incurred: number;
  status: string;
  loss_description?: string;
  claimant_name?: string;
  litigation_flag?: boolean;
  loss_year?: number;
}

interface ProductMetric {
  product: string;
  claim_count: number;
  total_incurred: number;
  premium: number;
  loss_ratio_pct: number;
  open_claims: number;
}

interface RiskNarrative {
  title: string;
  content: string;
  type: 'info' | 'warning' | 'alert';
}

// Constants
const API_ENDPOINT = 'https://api.elevatenow.tech/query';

const COLORS = {
  navy: '#1e3a8a', navyLight: '#3b5998', navyDark: '#0f172a',
  white: '#ffffff', bgLight: '#f1f5f9', bgCard: '#ffffff',
  textPrimary: '#0f172a', textSecondary: '#64748b', border: '#cbd5e1',
  success: '#10b981', warning: '#f59e0b', error: '#ef4444', info: '#3b82f6'
};

// Semantic colors for severity-based display
const SEVERITY_COLORS = {
  severe: '#dc2626',    // Red - >$500K or loss ratio >100%
  high: '#ea580c',      // Orange - >$100K or loss ratio >80%
  medium: '#d97706',    // Amber - >$25K or loss ratio >50%
  low: '#16a34a',       // Green - healthy
  neutral: '#64748b'    // Gray - no concern
};

const PREMIUM_FIELD_MAPPINGS: Record<string, string[]> = {
  'Auto Liability': ['numAutoLiab', 'autoLiabilityPremium', 'alPremium'],
  'Auto Physical Damage': ['numAutoPhyDamage', 'autoPhysDamagePremium', 'apdPremium'],
  'General Liability': ['numCGL', 'generalLiabilityPremium', 'glPremium', 'numGL'],
  'Property': ['numProperty', 'propertyPremium', 'propPremium'],
  'Employment Practices Liability': ['numEPLI', 'epliPremium', 'eplPremium'],
  'Public Officials E&O': ['numEnO', 'publicOfficialsEOPremium', 'poeoePremium'],
  'Excess Liability': ['numExcess', 'excessLiabilityPremium', 'umbrellaExcessPremium'],
  'Workers Compensation': ['numWorkersComp', 'workersCompPremium', 'wcPremium'],
  'Inland Marine': ['numInlandMarine', 'inlandMarinePremium', 'imPremium'],
  'Law Enforcement Liability': ['numLawEnforcement', 'lawEnforcementPremium', 'lelPremium'],
};

// Utilities
const formatCurrency = (v: number | null | undefined): string => {
  if (v == null) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

const formatPercent = (v: number | null | undefined): string => v == null ? '0%' : v.toFixed(1) + '%';

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York'
  });
};

const formatCurrentDate = (): string => {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/New_York'
  });
};

const truncateText = (t: string | null | undefined, max: number): string => {
  if (!t) return '';
  return t.length <= max ? t : t.substring(0, max) + '...';
};

// Semantic color helpers
const getSeverityColor = (incurred: number): string => {
  if (incurred >= 500000) return SEVERITY_COLORS.severe;
  if (incurred >= 100000) return SEVERITY_COLORS.high;
  if (incurred >= 25000) return SEVERITY_COLORS.medium;
  return SEVERITY_COLORS.low;
};

const getLossRatioColor = (ratio: number): string => {
  if (ratio >= 100) return SEVERITY_COLORS.severe;
  if (ratio >= 80) return SEVERITY_COLORS.high;
  if (ratio >= 50) return SEVERITY_COLORS.medium;
  return SEVERITY_COLORS.low;
};

const getStatusColor = (status: string, litigation?: boolean): string => {
  if (litigation) return SEVERITY_COLORS.severe;
  const s = status?.toLowerCase() || '';
  if (s.includes('lit')) return SEVERITY_COLORS.severe;
  if (s === 'open') return SEVERITY_COLORS.medium;
  if (s === 'closed') return SEVERITY_COLORS.low;
  return SEVERITY_COLORS.neutral;
};

const extractPremiumFromSummary = (summary: Record<string, any>, productName: string): number => {
  const fields = PREMIUM_FIELD_MAPPINGS[productName] || [];
  for (const field of fields) {
    const val = summary[field];
    if (val != null && val !== 0) {
      const num = typeof val === 'number' ? val : parseFloat(val);
      if (!isNaN(num)) return num;
    }
  }
  return 0;
};

// ============================================================================
// HEADER COMPONENT - Compact header WITH KPIs (matching mockup)
// ============================================================================
const DashboardHeader: React.FC<{
  insuredName: string; appId: string; agencyName: string;
  onClose: () => void;
  policies?: any[]; currentPolicyIndex: number; onPolicyChange: (index: number) => void;
  onDownload: () => void;
  lossRatio: number;
  totalIncurred: number;
  totalClaims: number;
  openClaims: number;
}> = ({ insuredName, appId, agencyName, onClose, policies, currentPolicyIndex, onPolicyChange, onDownload, lossRatio, totalIncurred, totalClaims, openClaims }) => {
  const showPolicyNav = policies && policies.length > 1;
  const ratioColor = getLossRatioColor(lossRatio);
  
  return (
    <Box sx={{ bgcolor: COLORS.navy, color: COLORS.white }}>
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1.5,
        minHeight: 64
      }}>
        {/* Left: Logo + Insured Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 32, height: 32, bgcolor: '#fff', borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Typography sx={{ color: COLORS.navy, fontWeight: 900, fontSize: '0.75rem' }}>EN</Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: 0.5, lineHeight: 1.2 }}>
              {insuredName}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
              APP: {appId} | {agencyName}
            </Typography>
          </Box>
        </Box>

        {/* Center: Policy Navigation (if multiple) */}
        {showPolicyNav && (
          <Box sx={{ 
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.75
          }}>
            <IconButton 
              size="small" 
              onClick={() => onPolicyChange(currentPolicyIndex - 1)}
              disabled={currentPolicyIndex === 0}
              sx={{ color: '#fff', p: 0.25 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, minWidth: 60, textAlign: 'center' }}>
              Policy {currentPolicyIndex + 1}/{policies.length}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => onPolicyChange(currentPolicyIndex + 1)}
              disabled={currentPolicyIndex === policies.length - 1}
              sx={{ color: '#fff', p: 0.25 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Right: KPIs + Date + Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          {/* Loss Ratio */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.6rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>
              Loss Ratio
            </Typography>
            <Typography sx={{ 
              fontSize: '1.5rem', 
              fontWeight: 800, 
              color: ratioColor,
              fontFamily: 'monospace',
              lineHeight: 1.1
            }}>
              {formatPercent(lossRatio)}
            </Typography>
          </Box>
          
          {/* Total Incurred */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.6rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>
              Total Incurred
            </Typography>
            <Typography sx={{ 
              fontSize: '1.5rem', 
              fontWeight: 800, 
              color: COLORS.white,
              fontFamily: 'monospace',
              lineHeight: 1.1
            }}>
              {formatCurrency(totalIncurred)}
            </Typography>
          </Box>
          
          {/* Claims Count */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.6rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>
              Claims
            </Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.white, lineHeight: 1.1 }}>
              {totalClaims}
            </Typography>
          </Box>
          
          {/* Open Claims Badge - only if there are open claims */}
          {openClaims > 0 && (
            <Chip 
              label={`${openClaims} Open`} 
              size="small" 
              sx={{ 
                height: 22, 
                fontSize: '0.65rem', 
                bgcolor: SEVERITY_COLORS.medium, 
                color: COLORS.white
              }} 
            />
          )}
          
          {/* Divider */}
          <Box sx={{ width: 1, height: 36, bgcolor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />
          
          {/* Date */}
          <Typography sx={{ fontSize: '0.7rem', opacity: 0.8, whiteSpace: 'nowrap' }}>
            {formatCurrentDate()}
          </Typography>
          
          {/* Actions */}
          <IconButton onClick={onDownload} sx={{ color: '#fff', p: 0.5 }} size="small">
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: '#fff', p: 0.5 }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// RISK NARRATIVE BAR - Full-width narrative box below header
// ============================================================================
const RiskNarrativeBar: React.FC<{
  narratives: RiskNarrative[];
  isLoading: boolean;
  onOpenChat: () => void;
}> = ({ narratives, isLoading, onOpenChat }) => {
  
  return (
    <Paper sx={{ 
      mx: 3, mt: 2, mb: 2,
      border: '1px solid ' + COLORS.border, 
      borderRadius: 2, 
      bgcolor: COLORS.bgCard,
      overflow: 'hidden'
    }}>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AssessmentIcon sx={{ color: COLORS.navy, fontSize: 18 }} />
          <Typography sx={{ fontWeight: 700, color: COLORS.navy, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
            Risk Narrative
          </Typography>
          {isLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
          <IconButton 
            onClick={onOpenChat} 
            size="small" 
            sx={{ ml: 'auto', bgcolor: COLORS.navy, color: COLORS.white, '&:hover': { bgcolor: COLORS.navyLight }, width: 28, height: 28 }}
            title="Ask AI about this policy"
          >
            <MagicWandIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        
        {isLoading ? (
          <Box>
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="90%" height={16} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {narratives.slice(0, 2).map((n, i) => (
              <Box key={i} sx={{ 
                flex: 1,
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1.5,
                pl: 1.5,
                borderLeft: '3px solid ' + (n.type === 'alert' ? SEVERITY_COLORS.severe : n.type === 'warning' ? SEVERITY_COLORS.medium : COLORS.info)
              }}>
                <Box>
                  <Typography sx={{ 
                    fontWeight: 700, 
                    fontSize: '0.7rem', 
                    color: n.type === 'alert' ? SEVERITY_COLORS.severe : n.type === 'warning' ? SEVERITY_COLORS.medium : COLORS.info,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.25
                  }}>
                    {n.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: COLORS.textPrimary, lineHeight: 1.4 }}>
                    {n.content}
                  </Typography>
                </Box>
              </Box>
            ))}
            {narratives.length === 0 && !isLoading && (
              <Typography sx={{ fontSize: '0.8rem', color: COLORS.textSecondary, fontStyle: 'italic' }}>
                Click the magic wand to generate AI insights about this policy.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ============================================================================
// LOSS DEVELOPMENT CHART
// ============================================================================
const LossDevChart: React.FC<{ claimsByYear: Record<string, any[]> }> = ({ claimsByYear }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    const loadChart = async () => {
      const ChartModule = await import('chart.js/auto');
      const Chart = ChartModule.default;
      if (!canvasRef.current) return;
      if (chartRef.current) chartRef.current.destroy();

      const years = Object.keys(claimsByYear).sort();
      const incurredData = years.map(y => (claimsByYear[y] || []).reduce((sum: number, c: any) => sum + (c.total_incurred || 0), 0));

      chartRef.current = new Chart(canvasRef.current, {
        type: 'line',
        data: {
          labels: years,
          datasets: [{
            label: 'Total Incurred', data: incurredData,
            borderColor: COLORS.navy, backgroundColor: 'rgba(30, 58, 138, 0.1)',
            borderWidth: 3, fill: true, tension: 0.3,
            pointBackgroundColor: COLORS.navy, pointBorderColor: COLORS.white,
            pointBorderWidth: 2, pointRadius: 6, pointHoverRadius: 8
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: COLORS.navyDark,
              callbacks: { label: (ctx: any) => 'Incurred: ' + formatCurrency(ctx.parsed.y) }
            }
          },
          scales: {
            x: { grid: { display: false }, ticks: { color: COLORS.textSecondary, font: { size: 11, weight: 'bold' } } },
            y: {
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: {
                color: COLORS.textSecondary,
                callback: (v: any) => typeof v === 'number' ? (v >= 1000000 ? '$' + (v/1000000).toFixed(1) + 'M' : v >= 1000 ? '$' + (v/1000).toFixed(0) + 'K' : '$' + v) : v
              },
              beginAtZero: true
            }
          }
        }
      });
    };
    loadChart();
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [claimsByYear]);

  return (
    <Paper sx={{ p: 2.5, border: '1px solid ' + COLORS.border, borderRadius: 2, bgcolor: COLORS.bgCard, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrendingUpIcon sx={{ color: COLORS.navy, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, color: COLORS.navy, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
          5-Year Loss Development Trend
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 180, position: 'relative' }}><canvas ref={canvasRef} /></Box>
    </Paper>
  );
};

// ============================================================================
// LIMIT CAPACITY / EXPOSURE CARD - With semantic coloring
// ============================================================================
const LimitCapacityCard: React.FC<{ 
  products: ProductMetric[]; 
  highestExposureClaim?: Claim;
  mostFrequentProduct?: { name: string; count: number };
}> = ({ products, highestExposureClaim, mostFrequentProduct }) => {
  // Sort by loss ratio descending, filter to those with premium
  const topProducts = products.filter(p => p.premium > 0).sort((a, b) => b.loss_ratio_pct - a.loss_ratio_pct).slice(0, 4);
  
  return (
    <Paper sx={{ p: 2.5, border: '1px solid ' + COLORS.border, borderRadius: 2, bgcolor: COLORS.bgCard, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <WarningIcon sx={{ color: COLORS.navy, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, color: COLORS.navy, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
          Limit Capacity / Exposure
        </Typography>
      </Box>
      
      {topProducts.map((p, i) => {
        const barColor = getLossRatioColor(p.loss_ratio_pct);
        const usedPct = formatPercent(p.loss_ratio_pct);
        return (
          <Box key={i} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.textPrimary }}>{p.product}</Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: barColor }}>
                {usedPct} Used
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(p.loss_ratio_pct, 100)} 
              sx={{
                height: 8, borderRadius: 4, bgcolor: COLORS.bgLight,
                '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 4 }
              }} 
            />
          </Box>
        );
      })}
      
      {/* Key Stats */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid ' + COLORS.border }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
              Highest Exposure Claim
            </Typography>
            {highestExposureClaim ? (
              <>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: getSeverityColor(highestExposureClaim.total_incurred) }}>
                  {formatCurrency(highestExposureClaim.total_incurred)}
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary }}>{highestExposureClaim.loss_number} • {highestExposureClaim.product}</Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: COLORS.textSecondary }}>No claims</Typography>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
              Most Frequent Product
            </Typography>
            {mostFrequentProduct ? (
              <>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: COLORS.navy, textDecoration: 'underline' }}>{mostFrequentProduct.name}</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: COLORS.textSecondary }}>{mostFrequentProduct.count} claims</Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: COLORS.textSecondary }}>No data</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// ============================================================================
// CLAIMS LEDGER - With semantic severity coloring
// ============================================================================
const ClaimsLedger: React.FC<{ 
  claims: Claim[]; 
  searchTerm: string; 
  onSearchChange: (t: string) => void;
  years: string[];
  products: string[];
}> = ({ claims, searchTerm, onSearchChange, years, products }) => {
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');

  const filtered = claims.filter(c => {
    if (yearFilter !== 'all' && String(c.loss_year) !== yearFilter) return false;
    if (productFilter !== 'all' && c.product !== productFilter) return false;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      return c.loss_number?.toLowerCase().includes(t) || 
             c.product?.toLowerCase().includes(t) || 
             c.loss_description?.toLowerCase().includes(t) || 
             c.claimant_name?.toLowerCase().includes(t);
    }
    return true;
  });

  const filteredTotal = filtered.reduce((sum, c) => sum + (c.total_incurred || 0), 0);
  const openCount = filtered.filter(c => c.status?.toLowerCase() === 'open' || c.litigation_flag).length;
  const severeCount = filtered.filter(c => (c.total_incurred || 0) >= 100000).length;

  // Get row background color based on severity
  const getRowBgColor = (claim: Claim): string => {
    if (claim.litigation_flag) return 'rgba(220, 38, 38, 0.08)'; // Red tint for litigation
    if ((claim.total_incurred || 0) >= 500000) return 'rgba(220, 38, 38, 0.06)'; // Severe
    if ((claim.total_incurred || 0) >= 100000) return 'rgba(234, 88, 12, 0.05)'; // High
    return 'transparent';
  };

  return (
    <Paper sx={{ border: '1px solid ' + COLORS.border, borderRadius: 2, bgcolor: COLORS.bgCard, overflow: 'hidden' }}>
      {/* Header with Filters */}
      <Box sx={{ p: 2, borderBottom: '1px solid ' + COLORS.border, bgcolor: COLORS.bgLight }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 700, color: COLORS.navy, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              Detailed Loss Ledger
            </Typography>
            <Chip label={`${filtered.length} claims`} size="small" sx={{ bgcolor: COLORS.navy, color: COLORS.white, fontSize: '0.65rem', height: 22 }} />
            <Chip label={formatCurrency(filteredTotal)} size="small" sx={{ bgcolor: COLORS.white, color: COLORS.textPrimary, fontSize: '0.65rem', fontWeight: 600, height: 22, border: '1px solid ' + COLORS.border }} />
            {severeCount > 0 && (
              <Chip label={`${severeCount} Severe`} size="small" sx={{ bgcolor: SEVERITY_COLORS.severe, color: COLORS.white, fontSize: '0.65rem', height: 22 }} />
            )}
            {openCount > 0 && (
              <Chip label={`${openCount} Open`} size="small" sx={{ bgcolor: SEVERITY_COLORS.medium, color: COLORS.white, fontSize: '0.65rem', height: 22 }} />
            )}
          </Box>
          
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Year Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.65rem', color: COLORS.textSecondary, fontWeight: 600 }}>Year:</Typography>
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                <Chip 
                  label="All" size="small" onClick={() => setYearFilter('all')}
                  sx={{ bgcolor: yearFilter === 'all' ? COLORS.navy : COLORS.white, color: yearFilter === 'all' ? COLORS.white : COLORS.textPrimary, fontSize: '0.6rem', height: 22, cursor: 'pointer', '&:hover': { opacity: 0.8 } }} 
                />
                {years.slice(0, 5).map(year => (
                  <Chip key={year} label={year} size="small" onClick={() => setYearFilter(year)}
                    sx={{ bgcolor: yearFilter === year ? COLORS.navy : COLORS.white, color: yearFilter === year ? COLORS.white : COLORS.textPrimary, fontSize: '0.6rem', height: 22, cursor: 'pointer', '&:hover': { opacity: 0.8 } }} 
                  />
                ))}
              </Box>
            </Box>

            {/* Product Filter */}
            <TextField
              select size="small" value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: 26, bgcolor: COLORS.white }, '& .MuiOutlinedInput-input': { py: 0.5, color: COLORS.textPrimary } }}
            >
              <option value="all">All Products</option>
              {products.map(p => (<option key={p} value={p}>{p}</option>))}
            </TextField>

            {/* Search */}
            <TextField 
              size="small" placeholder="Search..." value={searchTerm} onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{ 
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: COLORS.textSecondary, fontSize: 16 }} /></InputAdornment>, 
                sx: { fontSize: '0.7rem', bgcolor: COLORS.white, '& fieldset': { borderColor: COLORS.border }, borderRadius: 1, height: 26, '& input': { color: COLORS.textPrimary, py: 0.5 } } 
              }}
              sx={{ width: 160 }}
            />

            {(yearFilter !== 'all' || productFilter !== 'all' || searchTerm) && (
              <Button size="small" onClick={() => { setYearFilter('all'); setProductFilter('all'); onSearchChange(''); }}
                sx={{ fontSize: '0.65rem', textTransform: 'none', color: COLORS.error, minWidth: 'auto', p: 0.5 }}>Reset</Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Table with semantic row coloring */}
      <TableContainer sx={{ maxHeight: 350 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', bgcolor: COLORS.white, py: 1 }}>Year</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', bgcolor: COLORS.white, py: 1 }}>Claim ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', bgcolor: COLORS.white, py: 1 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', bgcolor: COLORS.white, py: 1, width: '35%' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', bgcolor: COLORS.white, py: 1 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', bgcolor: COLORS.white, py: 1 }} align="right">Incurred</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8rem' }}>
                    {(yearFilter !== 'all' || productFilter !== 'all' || searchTerm) ? 'No claims match filters' : 'No claims found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c, i) => {
                const incurredColor = getSeverityColor(c.total_incurred || 0);
                const statusColor = getStatusColor(c.status, c.litigation_flag);
                const rowBg = getRowBgColor(c);
                
                return (
                  <TableRow 
                    key={c.loss_number + '-' + i} 
                    sx={{ 
                      bgcolor: rowBg,
                      '&:hover': { bgcolor: 'rgba(30, 58, 138, 0.04)' }
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.navy }}>{c.loss_year}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: COLORS.textPrimary }}>{c.loss_number || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textPrimary }}>{c.product || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>{truncateText(c.loss_description, 70) || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Chip 
                        label={c.litigation_flag ? 'LITIGATION' : c.status?.toUpperCase() || 'UNKNOWN'} 
                        size="small" 
                        sx={{ 
                          bgcolor: statusColor, 
                          color: COLORS.white, 
                          fontWeight: 600, 
                          fontSize: '0.55rem', 
                          height: 20,
                          minWidth: 60
                        }} 
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Typography sx={{ 
                        fontWeight: 700, 
                        fontSize: '0.8rem', 
                        color: incurredColor,
                        fontFamily: 'monospace'
                      }}>
                        {formatCurrency(c.total_incurred)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ============================================================================
// MAGIC WAND DRAWER (unchanged functionality)
// ============================================================================
const MagicWandDrawer: React.FC<{ open: boolean; onClose: () => void; augmentedPolicy: any; insuredName: string }> = ({ open, onClose, augmentedPolicy, insuredName }) => {
  const [history, setHistory] = useState<{role: string; content: string}[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState<number | null>(null);
  const [lastPolicyId, setLastPolicyId] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history]);
  
  useEffect(() => {
    const currentPolicyId = augmentedPolicy?.appnum || augmentedPolicy?.Application?.submissionIdMaster || JSON.stringify(augmentedPolicy).substring(0, 100);
    if (currentPolicyId !== lastPolicyId) {
      setThreadId(Math.floor(Math.random() * 1000000000));
      setHistory([]);
      setInput('');
      setLastPolicyId(currentPolicyId);
    }
  }, [augmentedPolicy, lastPolicyId]);

  useEffect(() => { 
    if (open && !threadId) {
      setThreadId(Math.floor(Math.random() * 1000000000));
    }
  }, [open, threadId]);

  const send = async (customMessage?: string) => {
    const msg = customMessage || input.trim();
    if (!msg || loading) return;
    if (!customMessage) setInput('');
    setHistory(h => [...h, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const ctx = 'You are an expert insurance underwriter reviewing loss run data for ' + insuredName + '.\n\nUSER QUESTION: ' + msg + '\n\nFULL POLICY DATA:\n' + JSON.stringify(augmentedPolicy, null, 2) + '\n\nProvide a clear, actionable response. No markdown formatting.';
      const res = await fetch(API_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ agent_id: '328e1efd-c171-4365-8a7e-00f277e142e5', message: ctx, thread_id: threadId }) });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      let resp = (data.response || data.message || '').replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,6}\s/g, '').replace(/`{1,3}/g, '').trim();
      setHistory(h => [...h, { role: 'assistant', content: resp }]);
    } catch (e) { 
      setHistory(h => [...h, { role: 'assistant', content: 'Sorry, an error occurred. Please try again.' }]); 
    }
    setLoading(false);
  };

  const quickQuestions = [
    { label: 'Summarize loss history', q: 'Give me an executive summary of this loss run - key metrics, trends, and concerns.', autoSend: true },
    { label: 'List all claims', q: 'List all claims with their claim number, date, product, status, and incurred amount.', autoSend: true },
    { label: 'Explain a claim', q: 'Explain claim # in detail - what happened, what was paid, and how was it detected?', autoSend: false },
    { label: 'Data quality check', q: 'How accurate is this extraction? Are there any unmatched or suspicious claims?', autoSend: true },
    { label: 'Compare products', q: 'Compare loss ratios across product lines. Which lines are most concerning?', autoSend: true },
    { label: 'Open claims risk', q: 'What is the total reserve exposure from open claims? Any litigation?', autoSend: true },
    { label: 'Show provenance', q: 'How did you extract and match the claims data? Show me the detection methods used.', autoSend: true }
  ];

  const handleQuickQuestion = (q: typeof quickQuestions[0]) => {
    if (q.autoSend) { send(q.q); } else { setInput(q.q); }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: 1400 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, height: '100%', display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: COLORS.navy, color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MagicWandIcon /><Typography sx={{ fontWeight: 600 }}>Ask the Expert</Typography></Box>
        <IconButton onClick={onClose} sx={{ color: COLORS.white }} size="small"><CloseIcon /></IconButton>
      </Box>
      
      {/* Chat Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: COLORS.bgLight, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {history.length === 0 && !loading && (
          <Box sx={{ py: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <MagicWandIcon sx={{ fontSize: 40, color: COLORS.navy, opacity: 0.7, mb: 1 }} />
              <Typography sx={{ fontWeight: 600, color: COLORS.navy, mb: 0.5 }}>Ask the Expert</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: COLORS.textSecondary }}>Get AI-powered insights about this loss run</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.textSecondary, mb: 1.5, fontWeight: 600, textTransform: 'uppercase' }}>Suggested questions:</Typography>
            <Stack spacing={1}>
              {quickQuestions.map((q, i) => (
                <Paper key={i} elevation={0} onClick={() => handleQuickQuestion(q)}
                  sx={{ p: 1.5, cursor: 'pointer', border: '1px solid ' + COLORS.border, borderRadius: 2, bgcolor: COLORS.white, '&:hover': { bgcolor: COLORS.bgLight, borderColor: COLORS.navy } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: COLORS.navy }}>{q.label}</Typography>
                    {!q.autoSend && (<Chip label="edit" size="small" sx={{ fontSize: '0.6rem', height: 16, bgcolor: COLORS.warning, color: COLORS.white }} />)}
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>{q.q}</Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
        
        {history.map((m, i) => (
          <Box key={i} sx={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: m.role === 'user' ? COLORS.navy : COLORS.white, color: m.role === 'user' ? COLORS.white : COLORS.textPrimary, borderRadius: 2, border: m.role === 'assistant' ? '1px solid ' + COLORS.border : 'none' }}>
              <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
            </Paper>
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.textSecondary }}>
            <CircularProgress size={16} /><Typography variant="body2" sx={{ fontStyle: 'italic' }}>Analyzing...</Typography>
          </Box>
        )}
        <div ref={endRef} />
      </Box>
      
      {/* Quick Questions */}
      {history.length > 0 && history.length < 6 && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: COLORS.white, borderTop: '1px solid ' + COLORS.border }}>
          <Typography variant="caption" sx={{ color: COLORS.textSecondary, mb: 1, display: 'block', fontWeight: 600 }}>Follow-up questions:</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
            {quickQuestions.filter(q => q.autoSend).slice(0, 4).map((q, i) => (
              <Chip key={i} label={q.label} size="small" onClick={() => handleQuickQuestion(q)} 
                sx={{ bgcolor: COLORS.bgLight, color: COLORS.textPrimary, border: '1px solid ' + COLORS.border, fontSize: '0.7rem', cursor: 'pointer', '&:hover': { bgcolor: COLORS.navy, color: COLORS.white } }} />
            ))}
          </Stack>
        </Box>
      )}
      
      {/* Input Area */}
      <Box sx={{ p: 2, borderTop: '1px solid ' + COLORS.border, bgcolor: COLORS.white, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField fullWidth size="small" placeholder="Ask about this loss run..." value={input} onChange={(e) => setInput(e.target.value)} 
            onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            disabled={loading} sx={{ '& .MuiOutlinedInput-root': { bgcolor: COLORS.bgLight }, '& input': { color: COLORS.textPrimary } }} />
          <IconButton onClick={() => send()} disabled={loading || !input.trim()} 
            sx={{ bgcolor: COLORS.navy, color: COLORS.white, '&:hover': { bgcolor: COLORS.navyLight }, '&:disabled': { bgcolor: COLORS.border } }}><SendIcon /></IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
const LossRunDashboard: React.FC<LossRunDashboardProps> = ({ augmentedPolicy, record, policies: externalPolicies, onClose }) => {
  const [currentPolicyIndex, setCurrentPolicyIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [narratives, setNarratives] = useState<RiskNarrative[]>([]);
  const [narrativesLoading, setNarrativesLoading] = useState(true);

  // Determine all policies available
  const allPolicies = externalPolicies || (record?.policies) || (augmentedPolicy ? [augmentedPolicy] : []);
  
  // Handle augmented_policy wrapper for batch data
  const currentPolicy = allPolicies[currentPolicyIndex]?.augmented_policy || allPolicies[currentPolicyIndex] || augmentedPolicy;
  
  // Extract data from current policy
  const jsonData = currentPolicy?.jsonData || currentPolicy || {};
  const appInfo = jsonData?.Application || currentPolicy?.Application || {};
  const submissionInfo = jsonData?.SubmissionInfo || {};
  const summaryNode = jsonData?.Summary || {};
  const lossRuns = jsonData?.loss_runs || currentPolicy?.loss_runs || {};
  const claimsByYear = lossRuns?.claims_by_year || {};
  const derivedAttrs = lossRuns?.derived_attributes || {};
  const derivedSummary = derivedAttrs?.summary || {};
  const byProduct = derivedAttrs?.by_product || {};
  const metadata = lossRuns?._metadata || {};

  // Extract insured name - check multiple locations (ORIGINAL LOGIC)
  const insuredName = 
    appInfo?.InsuredFullName || 
    appInfo?.hdEntityName ||
    jsonData?.GeneralInfo?.entityFullName ||
    submissionInfo?.txtInsuredName ||
    currentPolicy?.InsuredFullName ||
    currentPolicy?.insuredName ||
    'Unknown Insured';
  
  const appId = currentPolicy?.appnum || allPolicies[currentPolicyIndex]?.appnum || appInfo?.submissionIdMaster || jsonData?.appnum || 'N/A';
  
  const agencyName = 
    appInfo?.AgencyFullName || 
    appInfo?.hdAgencyName ||
    'Unknown Agency';

  // Compute metrics from claims_by_year (fallback when derived_attributes is empty)
  // CRITICAL FIX: Match original extraction logic
  const computedMetrics = useMemo(() => {
    const allClaims: Claim[] = [];
    Object.entries(claimsByYear).forEach(([year, claims]: [string, any]) => {
      if (Array.isArray(claims)) {
        claims.forEach((c: any) => {
          allClaims.push({
            loss_number: c.loss_number,
            date_of_loss: c.date_of_loss,
            product: c.product,
            total_incurred: c.total_incurred || c.net_incurred || 0,
            status: c.status,
            loss_description: c.loss_description,
            claimant_name: c.claimant_name,
            litigation_flag: c.litigation_flag,
            loss_year: c.loss_year || parseInt(year)
          });
        });
      }
    });
    allClaims.sort((a, b) => (b.total_incurred || 0) - (a.total_incurred || 0));

    const totalIncurredFromClaims = allClaims.reduce((sum, c) => sum + (c.total_incurred || 0), 0);
    
    // Build product stats from claims if byProduct is empty
    let productStats: Record<string, any> = {};
    if (Object.keys(byProduct).length === 0 && allClaims.length > 0) {
      allClaims.forEach(c => {
        if (c.product) {
          if (!productStats[c.product]) {
            productStats[c.product] = {
              claim_count: 0,
              total_incurred: 0,
              open_claims: 0,
              closed_claims: 0,
              premium: extractPremiumFromSummary(summaryNode, c.product)
            };
          }
          productStats[c.product].claim_count += 1;
          productStats[c.product].total_incurred += c.total_incurred || 0;
          if (c.status?.toLowerCase() === 'open' || c.litigation_flag) {
            productStats[c.product].open_claims += 1;
          } else {
            productStats[c.product].closed_claims += 1;
          }
        }
      });
      
      // Calculate loss ratios for each product
      Object.keys(productStats).forEach(prod => {
        const stats = productStats[prod];
        if (stats.premium > 0) {
          stats.loss_ratio_pct = (stats.total_incurred / stats.premium) * 100;
        } else {
          stats.loss_ratio_pct = 0;
        }
      });
    } else {
      productStats = byProduct;
    }

    // Calculate total premium - ALL active products (matches reconciliation report)
    let totalPremium = 0;
    const activeProducts = lossRuns?.active_products || Object.keys(productStats);
    activeProducts.forEach((prod: string) => {
      const premium = productStats[prod]?.premium || extractPremiumFromSummary(summaryNode, prod);
      if (productStats[prod]) {
        productStats[prod].premium = premium;
      }
      // Add premium for ALL active products, not just those with claims
      totalPremium += premium;
    });

    const totalIncurred = derivedSummary?.total_incurred || totalIncurredFromClaims;
    const lossRatio = totalPremium > 0 ? (totalIncurred / totalPremium) * 100 : 0;

    const productMetrics: ProductMetric[] = activeProducts.map((name: string) => {
      const data = productStats[name] || {};
      const premium = data.premium || extractPremiumFromSummary(summaryNode, name);
      const incurred = data.total_incurred || 0;
      return {
        product: name,
        claim_count: data.claim_count || 0,
        total_incurred: incurred,
        premium: premium,
        loss_ratio_pct: premium > 0 ? (incurred / premium) * 100 : (data.loss_ratio_pct || 0),
        open_claims: data.open_claims || 0
      };
    });

    const highestExposureClaim = allClaims.length > 0 ? allClaims[0] : undefined;
    const productCounts: Record<string, number> = {};
    allClaims.forEach(c => {
      if (c.product) productCounts[c.product] = (productCounts[c.product] || 0) + 1;
    });
    const mostFrequentProduct = Object.entries(productCounts).length > 0 
      ? Object.entries(productCounts).sort((a, b) => b[1] - a[1])[0]
      : undefined;

    const openClaims = allClaims.filter(c => c.status?.toLowerCase() === 'open' || c.litigation_flag).length;

    return {
      allClaims,
      totalIncurred,
      totalPremium,
      lossRatio,
      productMetrics,
      highestExposureClaim,
      mostFrequentProduct: mostFrequentProduct ? { name: mostFrequentProduct[0], count: mostFrequentProduct[1] } : undefined,
      openClaims
    };
  }, [claimsByYear, byProduct, derivedSummary, lossRuns?.active_products, summaryNode]);

  const lossRatio = derivedSummary?.blended_loss_and_alae_ratio_pct || computedMetrics.lossRatio;
  const totalIncurred = derivedSummary?.total_incurred || computedMetrics.totalIncurred;
  const allClaims = computedMetrics.allClaims;
  const productMetrics = computedMetrics.productMetrics;
  const highestExposureClaim = computedMetrics.highestExposureClaim;
  const mostFrequentProduct = computedMetrics.mostFrequentProduct;

  const uniqueYears = Array.from(new Set(allClaims.map(c => String(c.loss_year)))).sort((a, b) => Number(b) - Number(a));
  const uniqueProducts = Array.from(new Set(allClaims.map(c => c.product).filter(Boolean) as string[])).sort();

  // Stable policy ID for narrative fetch
  const policyId = useMemo(() => {
    return currentPolicy?.appnum || 
           currentPolicy?.jsonData?.SubmissionInfo?.txtPolicyNumber ||
           JSON.stringify(currentPolicy).substring(0, 50);
  }, [currentPolicy]);

  useEffect(() => {
    const fetchNarratives = async () => {
      setNarrativesLoading(true);
      try {
        const analysisSummary = {
          insured: insuredName,
          loss_ratio_pct: lossRatio,
          total_incurred: totalIncurred,
          total_claims: allClaims.length,
          open_claims: computedMetrics.openClaims,
          products: productMetrics.filter(p => p.claim_count > 0).map(p => ({
            name: p.product, claims: p.claim_count, incurred: p.total_incurred, loss_ratio: p.loss_ratio_pct
          })),
          top_claims: allClaims.slice(0, 5).map(c => ({
            id: c.loss_number, product: c.product, incurred: c.total_incurred, description: c.loss_description?.substring(0, 100)
          }))
        };
        const prompt = 'You are an expert insurance underwriter. Analyze this loss run data and provide exactly 2 risk narratives.\n\nDATA:\n' + JSON.stringify(analysisSummary, null, 2) + '\n\nRespond with ONLY a JSON array of exactly 2 objects in this format (no markdown, no explanation):\n[\n  {"title": "SHORT TITLE", "content": "2-3 sentence analysis", "type": "info|warning|alert"},\n  {"title": "SHORT TITLE", "content": "2-3 sentence analysis", "type": "info|warning|alert"}\n]\n\nFirst narrative should address the primary line of business performance.\nSecond should identify a specific risk pattern or concern from the claims.\nUse "alert" for loss ratios >80%, "warning" for >50%, "info" otherwise.';
        const res = await fetch(API_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ agent_id: '328e1efd-c171-4365-8a7e-00f277e142e5', message: prompt, thread_id: Math.floor(Math.random() * 1000000000) }) });
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        const txt = data.response || data.message || '';
        const match = txt.match(/\[[\s\S]*\]/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setNarratives(parsed);
            setNarrativesLoading(false);
            return;
          }
        }
        throw new Error('Parse error');
      } catch (e) {
        setNarratives([
          { title: 'Loss Performance', content: 'Overall loss ratio of ' + formatPercent(lossRatio) + ' with ' + formatCurrency(totalIncurred) + ' total incurred across ' + allClaims.length + ' claims.', type: lossRatio > 80 ? 'alert' : lossRatio > 50 ? 'warning' : 'info' },
          { title: 'Claims Summary', content: computedMetrics.openClaims + ' open claims require monitoring. ' + (allClaims.length - computedMetrics.openClaims) + ' claims have been resolved.', type: computedMetrics.openClaims > 3 ? 'warning' : 'info' }
        ]);
      }
      setNarrativesLoading(false);
    };
    fetchNarratives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPolicyIndex, policyId]);

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(currentPolicy, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = appId + '_LossRunOutput.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const handlePolicyChange = (index: number) => {
    setCurrentPolicyIndex(index);
    setChatOpen(false);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: COLORS.bgLight, overflow: 'hidden' }}>
      {/* Header with KPIs */}
      <DashboardHeader 
        insuredName={insuredName} 
        appId={appId} 
        agencyName={agencyName} 
        onClose={onClose}
        policies={allPolicies.length > 1 ? allPolicies : undefined}
        currentPolicyIndex={currentPolicyIndex}
        onPolicyChange={handlePolicyChange}
        onDownload={handleDownload}
        lossRatio={lossRatio}
        totalIncurred={totalIncurred}
        totalClaims={allClaims.length}
        openClaims={computedMetrics.openClaims}
      />
      
      {/* Risk Narrative Bar - Full width below header */}
      <RiskNarrativeBar
        narratives={narratives}
        isLoading={narrativesLoading}
        onOpenChat={() => setChatOpen(true)}
      />
      
      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 3 }}>
        {/* Charts Row - Loss Dev + Exposure SIDE BY SIDE */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' }, gap: 2, mb: 2 }}>
          <LossDevChart claimsByYear={claimsByYear} />
          <LimitCapacityCard 
            products={productMetrics} 
            highestExposureClaim={highestExposureClaim}
            mostFrequentProduct={mostFrequentProduct}
          />
        </Box>
        
        {/* Claims Ledger - With Semantic Coloring */}
        <ClaimsLedger 
          claims={allClaims} 
          searchTerm={searchTerm} 
          onSearchChange={setSearchTerm}
          years={uniqueYears}
          products={uniqueProducts}
        />
      </Box>
      
      {/* Footer Bar */}
      <Box sx={{ 
        px: 3, py: 1.5, 
        bgcolor: COLORS.white, 
        borderTop: '1px solid ' + COLORS.border, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label={'Schema v' + (metadata.schema_version || '3.7')} size="small" variant="outlined" sx={{ borderColor: COLORS.border, color: COLORS.textSecondary, fontSize: '0.65rem', height: 22 }} />
          {metadata.inferenced_count > 0 && (
            <Chip label={metadata.inferenced_count + ' AI-Mapped'} size="small" sx={{ bgcolor: COLORS.info, color: COLORS.white, fontSize: '0.65rem', height: 22 }} />
          )}
        </Box>
        <Typography sx={{ fontSize: '0.65rem', color: COLORS.textSecondary }}>
          ElevateNow Loss Run Intelligence
        </Typography>
      </Box>
      
      <MagicWandDrawer open={chatOpen} onClose={() => setChatOpen(false)} augmentedPolicy={currentPolicy} insuredName={insuredName} />
    </Box>
  );
};

export default LossRunDashboard;